import React, {useMemo, useState} from "react";
import csvDownload from "json-to-csv-export";
import {isDesktop} from "react-device-detect";
import {IconButton} from "@mui/material";
import {Chart} from "react-google-charts";
import {Delete as DeleteIcon, Download as DownloadIcon, Edit as EditIcon} from "@mui/icons-material";
import {useFiltered} from "@atttomyx/shared-hooks";
import {
    Cards,
    ConfirmDeleteDialog,
    FiltersAccordion,
    FloatingAddButton,
    FloatingButton
} from "@atttomyx/react-components";
import {OfferingCard} from "../cards";
import {OfferingDialog} from "../dialogs";
import {OfferingFilters} from "../filters";
import {offeringService} from "../../services";
import {arrays, money, objects, users} from "@atttomyx/shared-utils";
import {offeringUtils} from "../../utils";
import {colors, themes} from "@atttomyx/shared-constants";
import {
    CSV_DELIMITER,
    CSV_FIELD_AMOUNT,
    CSV_FIELD_CATEGORY,
    CSV_FIELD_DATE,
    CSV_FIELD_MEMBER,
    CSV_FIELD_TYPE,
    CSV_FILENAME_OFFERINGS,
    CSV_HEADERS_OFFERINGS,
    NODE_ANONYMOUS
} from "../../constants";
import "./offeringsPage.css";

const OPTIONS = {
    chartArea: { width: "80%", height: "80%" },
    pieSliceText: "value",
    tooltip: {
        text: "percentage",
    },
    legend: {
        position: "bottom",
    },
};

const OfferingsPage = (props) => {
    const { dimensions, snackbar, user, unique, nodes, offerings, filters,
        onSave, onDelete } = props;
    const [ showFilters, setShowFilters ] = useState(false);
    const [ offering, setOffering ] = useState(null);
    const [ deleting, setDeleting ] = useState(null);

    const idToNode = useMemo(() => {
        return arrays.getIdToEntity(nodes);
    }, [nodes]);

    const sanitized = useMemo(() => {
        return offerings.map((offering) => {
            const node = idToNode[offering.nodeId];

            return node ? offering : offeringUtils.sanitizeAnonymous(offering);
        });
    }, [offerings, idToNode]);

    const filtered = useFiltered(sanitized, filters, offeringUtils.sortOfferings);

    const theme = useMemo(() => {
        return user.settings.theme === themes.DARK ? {
            backgroundColor: {
                fill: "#404040",
            },
            titleTextStyle: {
                color: colors.WHITE,
            },
            legend: {
                textStyle: {
                    color: colors.WHITE,
                },
            },
        } : {
            backgroundColor: {
                fill: "#F5F5F5",
            },
            titleTextStyle: {
                color: colors.BLACK,
            },
            legend: {
                textStyle: {
                    color: colors.BLACK,
                },
            },
        }
    }, [user]);

    const categoryOptions = useMemo(() => {
        return objects.deepMerge(OPTIONS, objects.deepMerge(theme, {
            title: "Offerings by Category",
        }));
    }, [theme]);

    const typeOptions = useMemo(() => {
        return objects.deepMerge(OPTIONS, objects.deepMerge(theme, {
            title: "Offerings by Type",
        }));
    }, [theme]);

    const categoryData = useMemo(() => {
        const summed = offeringUtils.sumByGrouping(filtered, "category");
        const data = [['Category', 'Amount']];

        Object.entries(summed).forEach(([category, amount]) => {
            data.push([category, money.toDouble(amount)]);
        });

        return data;
    }, [filtered]);

    const typeData = useMemo(() => {
        const summed = offeringUtils.sumByGrouping(filtered, "type");
        const data = [['Type', 'Amount']];

        Object.entries(summed).forEach(([category, amount]) => {
            data.push([category, money.toDouble(amount)]);
        });

        return data;
    }, [filtered]);

    const downloadCsv = () => {
        const lines = [];

        filtered.forEach(offering => {
            const node = idToNode[offering.nodeId];
            const contact = node ? node : NODE_ANONYMOUS;
            const name = users.getFullName(contact);

            arrays.addTo(lines, {
                [CSV_FIELD_DATE]: offering.date,
                [CSV_FIELD_CATEGORY]: offering.category,
                [CSV_FIELD_TYPE]: offering.type,
                [CSV_FIELD_AMOUNT]: money.toDouble(offering.amount),
                [CSV_FIELD_MEMBER]: name,
            });
        });

        csvDownload({
            data: lines,
            delimiter: CSV_DELIMITER,
            headers: CSV_HEADERS_OFFERINGS,
            filename: CSV_FILENAME_OFFERINGS,
        });
    };

    return <div className="offerings-page">
        <div className="charts-container">
            <div className="chart">
                <Chart
                    chartType="PieChart"
                    data={categoryData}
                    options={categoryOptions}
                    width="100%"
                    height="300px"
                />
            </div>
            <div className="chart">
                <Chart
                    chartType="PieChart"
                    data={typeData}
                    options={typeOptions}
                    width="100%"
                    height="300px"
                />
            </div>
        </div>
        <div className="filters">
            <FiltersAccordion
                filters={filters}
                form={OfferingFilters}
                formProps={{
                    unique: unique,
                    nodes: nodes,
                }}
                open={showFilters}
                onOpen={() => setShowFilters(true)}
                onClose={() => setShowFilters(false)}
            />
        </div>
        <Cards
            items={filtered}
            paging={filters.paging}
            dimensions={dimensions}
            renderer={(offering) => offering.id === deleting ? null :
                <OfferingCard key={offering.id} idToNode={idToNode} offering={offering} filters={filters}>
                    <IconButton
                        color="secondary"
                        title="Delete"
                        className="delete"
                        onClick={() => setDeleting(offering)}
                    >
                        <DeleteIcon/>
                    </IconButton>
                    <IconButton
                        color="primary"
                        title="Edit"
                        disabled={!offering.nodeId}
                        onClick={() => setOffering(offering)}
                    >
                        <EditIcon/>
                    </IconButton>
                </OfferingCard>}
        />
        <FloatingButton
            className="floating-download"
            icon={<DownloadIcon/>}
            title="Download CSV"
            position={!dimensions.landscape || !dimensions.short ? "higher" : undefined}
            center={!dimensions.short}
            onClick={downloadCsv}
            disabled={!isDesktop}
        />
        <FloatingAddButton
            className="floating-add"
            title="Add offering"
            position={!dimensions.landscape || !dimensions.short ? "higher" : undefined}
            center={!dimensions.short}
            onClick={() => setOffering({})}
        />
        {offering ? <OfferingDialog
            snackbar={snackbar}
            unique={unique}
            nodes={nodes}
            offering={offering}
            onCancel={() => setOffering(null)}
            onSave={(offering) => {
                setOffering(null);
                onSave(offering);
            }}
        /> : null}
        {deleting ? <ConfirmDeleteDialog
            snackbar={snackbar}
            title={`${deleting.category} ${deleting.type}`}
            onCancel={() => setDeleting(null)}
            onDelete={(offeringId) => {
                setDeleting(null);
                onDelete(offeringId);
            }}
            deleter={(success, failure) => offeringService.deleteOffering(deleting.id, success, failure)}
        /> : null}
    </div>
};

export default OfferingsPage;
